import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ImageIcon, Maximize2, ArrowLeft, Calendar, Images, Video, Camera } from 'lucide-react';
import { MandalaCorner } from '../../components/luxury/MandalaCorner';
import { Lightbox } from '../../components/luxury/Lightbox';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { api } from '../../App';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fmtDate = (s) => {
  if (!s) return '';
  try { return new Date(`${s}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return s; }
};

function Hero({ title, accent, subtitle, back }) {
  return (
    <section className="relative py-16 sm:py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
      <MandalaCorner position="top-left" className="opacity-20" />
      <MandalaCorner position="bottom-right" className="opacity-20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {back && (
          <Link to="/gallery" className="inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--lux-royal))] hover:underline mb-4 min-h-[44px]" data-testid="gallery-back-link">
            <ArrowLeft className="w-4 h-4" /> All albums
          </Link>
        )}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <h1 className="text-4xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-4 break-words" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="luxury-gallery-title">
            {title} {accent && <span className="text-[hsl(var(--lux-gold))]">{accent}</span>}
          </h1>
          {subtitle && <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto">{subtitle}</p>}
        </motion.div>
      </div>
    </section>
  );
}

function AlbumsIndex() {
  const [albums, setAlbums] = useState(null);
  const [error, setError] = useState('');
  const load = () => { setError(''); api.get('/api/gallery').then(d => setAlbums(d.albums || [])).catch(e => { setError(e.message); setAlbums([]); }); };
  useEffect(() => { load(); }, []);

  return (
    <div className="overflow-hidden">
      <Hero title="Community" accent="Gallery" subtitle="Capturing moments from our vibrant celebrations, cultural programs, and community gatherings." />
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {albums === null && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-loading">{[0, 1, 2].map(i => <Skeleton key={i} className="aspect-[4/3] rounded-xl" />)}</div>
          )}
          {error && (
            <div className="max-w-md mx-auto text-center" data-testid="gallery-error"><p className="text-[hsl(var(--lux-ink))]">{error}</p><Button variant="outline" className="mt-3" onClick={load}>Retry</Button></div>
          )}
          {albums && albums.length === 0 && !error && (
            <div className="max-w-xl mx-auto text-center py-10" data-testid="gallery-empty">
              <Camera className="w-12 h-12 mx-auto text-[hsl(var(--lux-gold))]" />
              <h2 className="mt-4 text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>Photo albums coming soon</h2>
              <p className="mt-2 text-[hsl(var(--lux-ink)/0.7)]">We are gathering photos from recent celebrations. Check back shortly, or follow us on social media for the latest pictures.</p>
            </div>
          )}
          {albums && albums.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-albums">
              {albums.map((a, idx) => (
                <motion.div key={a.album_id} {...fadeInUp} transition={{ delay: Math.min(idx, 6) * 0.08 }}>
                  <Link to={`/gallery/${a.album_id}`} className="block group focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--lux-gold))] rounded-[var(--lux-radius-card)]" data-testid={`gallery-album-${a.album_id}`}>
                    <div className="relative aspect-[4/3] rounded-[var(--lux-radius-card)] overflow-hidden shadow-[var(--lux-shadow-1)] group-hover:shadow-[var(--lux-shadow-2)] transition-shadow bg-[hsl(var(--lux-royal-soft))]">
                      {a.cover_url ? (
                        <img src={`${BACKEND}${a.cover_url}`} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-12 h-12 text-[hsl(var(--lux-royal)/0.4)]" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
                        <div className="absolute bottom-0 left-0 right-0 p-5">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className="bg-[hsl(var(--lux-gold))] text-white"><Images className="w-3 h-3 mr-1" />{a.photo_count} photo{a.photo_count === 1 ? '' : 's'}</Badge>
                            {a.video_count > 0 && <Badge className="bg-white/20 text-white backdrop-blur-sm"><Video className="w-3 h-3 mr-1" />{a.video_count}</Badge>}
                          </div>
                          <h3 className="text-white text-2xl font-bold leading-tight" style={{ fontFamily: 'Cormorant Garamond' }}>{a.title}</h3>
                          {a.event_date && <p className="text-white/80 text-sm mt-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(a.event_date)}</p>}
                        </div>
                      </div>
                      <div className="absolute inset-0 border-2 border-[hsl(var(--lux-gold)/0)] group-hover:border-[hsl(var(--lux-gold)/0.6)] rounded-[var(--lux-radius-card)] transition-colors pointer-events-none" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function AlbumView({ albumId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(-1);
  const load = () => { setError(''); api.get(`/api/gallery/${albumId}`).then(setData).catch(e => { setError(e.message || 'Album not found'); setData({ album: null, items: [] }); }); };
  useEffect(() => { load(); window.scrollTo(0, 0); }, [albumId]); // eslint-disable-line react-hooks/exhaustive-deps

  const album = data?.album;
  const items = data?.items || [];

  return (
    <div className="overflow-hidden">
      <Hero title={album?.title || (data ? 'Album not found' : 'Loading…')} subtitle={album ? [album.event_date ? fmtDate(album.event_date) : null, album.description].filter(Boolean).join(' · ') : ''} back />
      <section className="py-12 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {data === null && <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="gallery-album-loading">{[0, 1, 2, 3, 4, 5, 6, 7].map(i => <Skeleton key={i} className="aspect-square rounded-lg" />)}</div>}
          {error && (
            <div className="max-w-md mx-auto text-center" data-testid="gallery-album-error">
              <p className="text-[hsl(var(--lux-ink))]">This album isn't available. It may have been unpublished.</p>
              <Link to="/gallery"><Button variant="outline" className="mt-3">Back to albums</Button></Link>
            </div>
          )}
          {album && items.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="gallery-album-grid">
              {items.map((it, idx) => (
                <motion.button
                  type="button"
                  key={it.file_id}
                  {...fadeInUp}
                  transition={{ delay: Math.min(idx % 8, 7) * 0.05 }}
                  onClick={() => setOpen(idx)}
                  className="group relative aspect-square rounded-lg overflow-hidden shadow-[var(--lux-shadow-1)] hover:shadow-[var(--lux-shadow-2)] bg-[hsl(var(--lux-royal-soft))] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--lux-gold))]"
                  aria-label={it.caption || `Open photo ${idx + 1}`}
                  data-testid="gallery-photo"
                >
                  {it.kind === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center bg-black/80"><Video className="w-10 h-10 text-white/80" /></div>
                  ) : (
                    <img src={`${BACKEND}${it.thumb_url}`} alt={it.caption || ''} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    {it.caption && <p className="absolute bottom-0 left-0 right-0 p-3 text-white text-sm line-clamp-2">{it.caption}</p>}
                    <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"><Maximize2 className="w-4 h-4 text-white" /></div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>
      {open >= 0 && <Lightbox items={items} index={open} onClose={() => setOpen(-1)} onIndexChange={setOpen} />}
    </div>
  );
}

export default function LuxuryGallery() {
  const { albumId } = useParams();
  return albumId ? <AlbumView albumId={albumId} /> : <AlbumsIndex />;
}
